import { ParticipationType, PartyType } from 'generated/gql';
import { Link, Text } from '@rulebooks/evidend-titan';
import { t } from '@lingui/macro';
import dayjs from 'dayjs';
import type { Party } from 'generated/gql';
import { upperFirst } from 'lodash';

import { formatNaturalPersonName } from './formatName';
import { formatDate } from './formatShares';

const participationTypes = {
  [ParticipationType.Bailiff]: () => t`deurwaarder`,
  [ParticipationType.Guardian]: () => t`bewindvoerder`,
  [ParticipationType.Pledgee]: () => t`pandhouder`,
  [ParticipationType.RequiredInAttachment]: () => t`beslaglegger`,
  [ParticipationType.Usufructee]: () => t`vruchtgebruiker`,
  [ParticipationType.Historical]: () => t`geen huidige partij`,
};

export const formatParticipationType = (
  isFoundation: boolean,
  participationType: ParticipationType,
) => {
  if (participationType === ParticipationType.Shareholder)
    return upperFirst(isFoundation ? t`certificaathouder` : t`aandeelhouder`);
  return upperFirst(participationTypes[participationType]());
};

export const getPartyTypeUrl = (party: Pick<Party, 'type' | 'id'>) => {
  const partyTypeRef = party.type === PartyType.NaturalPerson ? 'persoon' : 'rechtspersoon';
  return `/${partyTypeRef}/${party.id}`;
};

export const getPartyParticipation = (party: Party) => {
  const { naturalPerson, legalEntity } = party;
  if (naturalPerson?.participation) return naturalPerson.participation;
  if (legalEntity?.participation) return legalEntity.participation;
};

export const getPartyName = (party: Party, compareDeceaseDate?: Date) => {
  const { naturalPerson, legalEntity } = party;
  if (naturalPerson) {
    const isDeceased = compareDeceaseDate
      ? naturalPerson.dateOfDecease &&
        naturalPerson.dateOfDecease <= dayjs(compareDeceaseDate).startOf('day').toDate()
      : naturalPerson.dateOfDecease;
    return formatNaturalPersonName(naturalPerson.name, isDeceased);
  }
  if (legalEntity) return legalEntity.name;
  return '';
};

export const getPartySublabel = ({ naturalPerson, legalEntity }: Party): string => {
  if (legalEntity) {
    return legalEntity.tradeRegisterNumber || '';
  }

  if (naturalPerson) {
    const { tag, dateOfBirth } = naturalPerson;
    if (tag) return tag;
    return formatDate(dateOfBirth);
  }
  return '';
};

interface IPartyLink {
  party: Party;
  isSublabelHidden?: boolean;
  compareDeceaseDate?: Date;
}
export const PartyLink = ({ party, isSublabelHidden = false, compareDeceaseDate }: IPartyLink) => {
  const sublabel = getPartySublabel(party);
  const name = getPartyName(party, compareDeceaseDate);
  return (
    <>
      <Link to={getPartyTypeUrl(party)}>{name}</Link>
      {!isSublabelHidden && sublabel && (
        <Text isBlock color="grey70" type="paragraphSmall">
          {sublabel}
        </Text>
      )}
    </>
  );
};
